import { FfsRegionClassification } from './ffsRegionClassificationSchema'
import { entryRouteToPartLevel, partEntryLevel } from './ffsRegionRouting'
import { FfsPart } from './ffsTypes'

export interface ComputeFfsEligibilityArgs {
  /** The FFS part the calc card is pinned to. */
  part: FfsPart
  /**
   * The LTA/region's committed classification, already parsed at the caller's
   * boundary. `null`/absent means "no usable decision" — absent, malformed, or
   * domain-inconsistent are deliberately NOT distinguished here, because all
   * three mean the same thing to an execution gate: the route is unknown.
   */
  classification?: FfsRegionClassification | null
}

export interface FfsEligibility {
  level1Eligible: boolean
  level2Eligible: boolean
  /** Human-readable reasons a level is disabled, surfaced as tab tooltips. */
  reasons?: {
    level1?: string
    level2?: string
  }
  /**
   * When true, every eligible level runs IMMEDIATELY — the "complete Level 1
   * first" ordering gate (`computeFfsLevelAvailability`) is bypassed. Set only for
   * an UNCLASSIFIED LTA (no pre-planning): Pre-Planning is skippable, so we do not
   * impose an L1→L2 ordering the engineer never opted into. Classified LTAs leave
   * it unset and keep the gate.
   */
  skipLevel1Gate?: boolean
}

const partLabel = (part: FfsPart): string =>
  part === FfsPart.PART_4 ? 'Part 4' : 'Part 5'

/** L1-entry unlocks both levels; L2-entry (B1/B2) unlocks only Level 2. */
function atEntryLevel(entryLevel: 'level1' | 'level2'): FfsEligibility {
  if (entryLevel === 'level1') {
    return { level1Eligible: true, level2Eligible: true }
  }
  return {
    level1Eligible: false,
    level2Eligible: true,
    reasons: {
      level1: 'Entry level for this component type is Level 2.',
    },
  }
}

/**
 * The per-LTA EXECUTION gate for an FFS part's levels: what the (pinned) calc
 * card may actually run. This UNLOCKS parts beyond the single routed one so an
 * engineer can run whichever FFS method is the appropriate next step, rather than
 * being forced into `entryRoute`'s part:
 *
 * - The **routed** part (from `entryRoute`) is always runnable, at its entry
 *   level (unchanged behaviour).
 * - Every **type-eligible** (part, level) is runnable in ANY order — there is no
 *   "Part 4 first" precondition. The SME's least-conservative-first workflow is a
 *   recommendation, not a lock.
 *
 * Availability is derived from the type matrix (`componentTypeEligibility`), NOT
 * from `entryRoute`-part equality — which was the old hard lock. Close-out
 * governing takes the BEST result across all eligible cells (any Pass ⇒ Pass),
 * so a non-routed assessment is first-class, not exploratory.
 *
 * PERMISSIVE on a missing classification: an LTA with no usable classification is
 * treated as a Level-1 entry (both levels eligible) so the engineer can still run
 * the assessment; the calc card surfaces the unvalidated route as a non-blocking
 * warning.
 */
export function computeFfsEligibility({
  part,
  classification,
}: ComputeFfsEligibilityArgs): FfsEligibility {
  if (!classification) {
    // Pre-Planning skipped/invalid: unlock EVERYTHING — both levels, immediately,
    // with no "complete Level 1 first" ordering (`skipLevel1Gate`). An LTA with no
    // committed route shouldn't be forced into a level order it never opted into.
    return { level1Eligible: true, level2Eligible: true, skipLevel1Gate: true }
  }

  const routed = entryRouteToPartLevel(classification.entryRoute)
  if (!routed) {
    // `entryRouteToPartLevel` returns null for two distinct terminals —
    // NEITHER_APPLICABLE (Type C, Level 3 required) and GROOVE_NOT_COVERED (a
    // groove-like flaw, assessed by a separate out-of-scope procedure) — so the
    // reason string must name which one actually applies.
    const reason =
      classification.entryRoute === 'GROOVE_NOT_COVERED'
        ? 'Groove-like flaw — assessed by a separate procedure outside this tool.'
        : 'This LTA is not FFS-eligible (Type C / no applicable route).'
    return {
      level1Eligible: false,
      level2Eligible: false,
      reasons: { level1: reason, level2: reason },
    }
  }

  // The routed part is always runnable, at the ROUTE's own level -- honoring an
  // explicit Level-2 route (the "permitted alternatives" headroom that
  // `validateFfsRegionClassification` accepts), not just the type's lowest level.
  if (routed.part === part) {
    return atEntryLevel(routed.level)
  }

  // Non-routed part: entry is the type's lowest eligible level, or `null` when
  // the type cannot be assessed in this part at all (e.g. B2 under Part 5).
  const entryLevel = partEntryLevel(classification.componentType, part)
  if (entryLevel === null) {
    const reason = `${partLabel(part)} is not an eligible assessment for a Type ${classification.componentType} region under API 579.`
    return {
      level1Eligible: false,
      level2Eligible: false,
      reasons: { level1: reason, level2: reason },
    }
  }

  return atEntryLevel(entryLevel)
}
